import React, { useEffect } from 'react';
import { AppState } from '../store/state';
import { AppDispatch } from '../store/store';
import { connect } from 'react-redux';
import { postUserKeywordsDTO, UpdateUserDTO, User } from '../store/user/types';
import {
  selectCurrentRole,
  selectCurrentUser,
  selectErrorState,
  selectIsRequiredInfoCompleted,
  selectKeywordsLevels,
  selectKeywordState,
  selectTrackUsingKeywords,
  selectUserKeywordsInCurrentTrack,
} from '../store/selectors';
import { Button } from '../components/ui';
import userSlice from '../store/user/slice';
import { useTranslation } from 'react-i18next';
import UserStepper, { Step } from '../components/UserStepper/UserStepper';
import ResearcherInfoForm, { ResearcherFormFields } from '../components/ResearcherInfoForm/ResearcherInfoForm';
import { validationErrorTransformer } from '../store/api';
import { ValidatedField } from '../store/api.d';
import { ConferenceSelectTrack, Role } from '../store/conference/types';
import conferenceSlice from '../store/conference/slice';
import Loading from '../components/Loading/Loading';
import KeywordPicker from '../components/KeywordPicker/KeywordPicker';
import { OpenSnackBarDTO } from '../store/error/types';
import errorSlice from '../store/error/slice';
import styled from 'styled-components';
import { isResearcherInfoCompleted } from '../helpers/users';
import keywordSlice from '../store/keyword/slice';

interface Props {
  user: User;
  role: Role | null;
  validationErrors: ValidatedField[];
  updateUserAction: (data: UpdateUserDTO) => void;
  isRequiredInfoCompleted: boolean;
  selectTrackAction: (data: ConferenceSelectTrack) => void;
  keywordState: KeywordState;
  postUserKeywordsAction: (data: postUserKeywordsDTO) => void;
  openSnackBarAction: (data: OpenSnackBarDTO) => void;
  clearValidationErrorsAction: () => void;
  trackUsingKeywords: boolean | null;
  userKeywords: number[];
  getKeywordsAction: () => void;
  keywordsLevels: KeywordsLevels;
}

const UserProfileWizardPage: React.FC<Props> = ({
  user,
  role,
  validationErrors,
  updateUserAction,
  isRequiredInfoCompleted,
  selectTrackAction,
  keywordState,
  postUserKeywordsAction,
  openSnackBarAction,
  clearValidationErrorsAction,
  trackUsingKeywords,
  userKeywords,
  getKeywordsAction,
  keywordsLevels,
}) => {
  const { t } = useTranslation();
  const hasKeywords = Object.keys(keywordState.keywordById).length > 0;

  useEffect(() => {
    if (role) {
      if (isRequiredInfoCompleted) {
        // We come here
        selectTrackAction({ trackId: role.track_id, roleId: role.id, doRedirect: true });
      } else if (trackUsingKeywords && role.keywords_required) {
        getKeywordsAction();
      }
    }
  }, [isRequiredInfoCompleted]);

  if (!role || (trackUsingKeywords && role.keywords_required && !hasKeywords)) return <Loading />;

  const getSteps = (step: number, totalSteps: number): Step[] => {
    const buttonText =
      totalSteps == 1 ? t('Complete') : t(`Complete {{step}} of {{total}}`, { step: step + 1, total: totalSteps });
    const steps: Step[] = [
      {
        content: (
          <ResearcherInfoForm
            role={role}
            formData={{
              dblp_key: user.person.dblp_key ?? '',
              google_scholar_id: user.person.google_scholar_id ?? '',
              orcid_id: user.person.orcid_id ?? '',
            }}
            onSave={(data) => {
              updateUserAction({
                ...user.person,
                ...data,
                include_role: true,
              });
            }}
            validation_error={validationErrorTransformer(validationErrors)}
            clearValidationErrorsAction={clearValidationErrorsAction}
          >
            <Button type="submit" className="w-48 place-self-center">
              <span className="px-14">{buttonText}</span>
            </Button>
          </ResearcherInfoForm>
        ),
        isCompleted: isResearcherInfoCompleted(user, role),
      },
    ];

    if (trackUsingKeywords && role.keywords_required) {
      steps.push({
        subtitle: t('Please, select those keywords that best match with your research interests.'),
        content: (
          <StyledKeywordPickerWrapper className="pl-32 mt-5 mobileCtrlUserProfileWizardKeywords">
            <KeywordPicker
              selectedIds={userKeywords}
              keywordById={keywordState.keywordById}
              keywordTree={keywordState.keywordTree}
              maxAllowed={5}
              onSelected={(selectedIds) => postUserKeywordsAction({ keyword_ids: selectedIds })}
              openSnackBarAction={openSnackBarAction}
              saveButtonTitle={buttonText}
              keywordsLevels={keywordsLevels}
            />
          </StyledKeywordPickerWrapper>
        ),
        isCompleted: userKeywords.length > 0,
      });
    }
    return steps;
  };

  return <UserStepper getSteps={getSteps} />;
};

const mapStateToProps = (state: AppState) => ({
  user: selectCurrentUser(state),
  role: selectCurrentRole(state),
  validationErrors: selectErrorState(state).validationErrors ?? [],
  isRequiredInfoCompleted: selectIsRequiredInfoCompleted(state),
  keywordState: selectKeywordState(state),
  trackUsingKeywords: selectTrackUsingKeywords(state),
  userKeywords: selectUserKeywordsInCurrentTrack(state),
  keywordsLevels: selectKeywordsLevels(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updateUserAction: (data: UpdateUserDTO) => dispatch(userSlice.actions.UPDATE(data)),
  selectTrackAction: (data: ConferenceSelectTrack) => dispatch(conferenceSlice.actions.SELECT_TRACK(data)),
  postUserKeywordsAction: (data: postUserKeywordsDTO) => dispatch(userSlice.actions['POST:KEYWORDS'](data)),
  openSnackBarAction: (data: OpenSnackBarDTO) => dispatch(errorSlice.actions['OPEN:SNACKBAR'](data)),
  clearValidationErrorsAction: () => dispatch(errorSlice.actions['CLEAR:VALIDATION_ERRORS']()),
  getKeywordsAction: () => dispatch(keywordSlice.actions['GET_KEYWORDS']()),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserProfileWizardPage);

const StyledKeywordPickerWrapper = styled.div`
  .keywordsFooterButtons {
    background-color: rgb(237, 243, 255);
    max-width: 56rem;
  }
`;
